<template>
  <!-- Riel de navegación vertical (estilo "icon rail"). Reemplaza al antiguo
  b-sidebar + a la barra de recursos superior del Navbar. El ícono superior
  expande/colapsa el riel; al pasar el mouse por un ícono se abre un flyout
  con las opciones de esa sección. -->
  <div v-if="user" class="kl-rail-root">
    <!-- Backdrop (solo móvil): cierra el menú al tocar fuera -->
    <div
      v-if="expanded"
      class="kl-rail__backdrop noprint"
      @click="expanded = false"
    ></div>
    <nav class="kl-rail noprint" :class="{ 'kl-rail--expanded': expanded }">
      <button
        class="kl-rail__toggle"
        type="button"
        :title="$t('reusable.toolbar.menu')"
        :aria-label="$t('reusable.toolbar.menu')"
        @click="expanded = !expanded"
      >
        <b-icon :icon="expanded ? 'x' : 'grid-3x3-gap-fill'"></b-icon>
        <span v-if="expanded" class="kl-rail__brand">{{
          $t("reusable.toolbar.menu")
        }}</span>
      </button>
      <div class="container-kl-menu">
        <div class="kl-rail__scroll">
          <template v-for="group in menuGroups">
            <!-- Grupo con enlace directo (sin flyout) -->
            <router-link
              v-if="group.to"
              :key="group.key"
              :to="{ name: group.to }"
              class="kl-rail__item kl-rail__direct"
              :class="{
                'is-active': activeGroupKey === group.key,
                'kl-rail__direct--primary': group.primary,
              }"
              :title="group.label"
              @click.native="handleNavigate"
            >
              <span class="kl-rail__icon"
                ><b-icon :icon="group.icon"></b-icon
              ></span>
              <span v-if="expanded" class="kl-rail__label">{{
                group.label
              }}</span>
            </router-link>

            <!-- Grupo con flyout de opciones -->
            <div
              v-else
              :key="group.key"
              class="kl-rail__item"
              :class="{
                'is-active': activeGroupKey === group.key,
                'is-open': openGroupKey === group.key,
              }"
              @click="toggleGroup(group.key)"
              @mouseenter="positionFlyout"
            >
              <div class="kl-rail__head">
                <span class="kl-rail__icon"
                  ><b-icon :icon="group.icon"></b-icon
                ></span>
                <span v-if="expanded" class="kl-rail__label">{{
                  group.label
                }}</span>
                <b-icon
                  v-if="expanded"
                  icon="chevron-right"
                  scale="0.7"
                  class="kl-rail__caret"
                ></b-icon>
              </div>

              <div
                class="kl-rail__flyout"
                :class="{ 'kl-rail__flyout--wide': group.items.length > 8 }"
              >
                <p class="kl-rail__flyout-title">{{ group.label }}</p>
                <div class="kl-rail__flyout-list">
                  <router-link
                    v-for="item in group.items"
                    :key="item.to"
                    :to="{ name: item.to }"
                    class="kl-rail__flyout-link"
                    @click.native.stop="handleNavigate"
                  >
                    <b-icon
                      icon="chevron-right"
                      scale="0.7"
                      class="kl-rail__flyout-chevron"
                    ></b-icon>
                    <span class="text-left">{{ item.label }}</span>
                  </router-link>
                </div>
              </div>
            </div>
          </template>
        </div>
        <NewAiComponent
          v-if="
            isLoged && institution && institution.kl_artificial_intelligence
          "
          class="kl-rail__bottom"
          :expanded="expanded"
        ></NewAiComponent>
      </div>
    </nav>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";

export default {
  name: "Toolbar",
  components: {
    NewAiComponent: () =>
      import("@/components/reusable/ai-component/NewAiComponent"),
  },
  data() {
    return {
      expanded: false,
      // Grupo abierto en el acordeón (vista móvil)
      openGroupKey: null,
    };
  },
  computed: {
    ...mapGetters({
      isLoged: "isLoged",
      institution: "getInstitution",
    }),
    ...mapState({
      user: "user",
    }),
    // ---- Helpers de permisos ----
    isSuper() {
      return !!(this.user && this.user.is_superuser);
    },
    notStudent() {
      return !!(
        this.user &&
        (!this.user.group_names.includes("ESTUDIANTES") || this.isSuper)
      );
    },
    isDuoc() {
      return !!(
        this.institution && this.institution.internal_use_id == "duoc.cl"
      );
    },
    isIacc() {
      return !!(
        this.institution && this.institution.internal_use_id == "iacc.cl"
      );
    },
    isDirectivoInst() {
      return !!(
        this.user &&
        (this.user.group_names.includes(
          "DIRECTIVOS ACADÉMICOS INSTITUCIONALES"
        ) ||
          this.isSuper)
      );
    },
    notProfStudent() {
      return !!(
        this.user &&
        (!["PROFESORES", "ESTUDIANTES"].some((g) =>
          this.user.group_names.includes(g)
        ) ||
          this.isSuper)
      );
    },
    // Las secciones que antes vivían en el b-sidebar (Gestión Administrativa,
    // Curricular/Pedagógica y Recursos Metodológicos) no se mostraban a
    // VISITANTES ni a ESTUDIANTES (salvo superusuario).
    canViewSidebarGroups() {
      if (!this.user) return false;
      if (this.user.group_names.includes("VISITANTES")) return false;
      if (this.user.group_names.includes("ESTUDIANTES") && !this.isSuper)
        return false;
      return true;
    },
    menuGroups() {
      if (!this.user) return [];
      const gvn = (key, plural, fallback) =>
        this.$getVisibleNames(key, plural, fallback);
      const someGroup = (groups) =>
        this.user &&
        (groups.some((g) => this.user.group_names.includes(g)) || this.isSuper);

      const academicGroups = [
        "DIRECTIVOS ACADÉMICOS INSTITUCIONALES",
        "DIRECTIVOS ACADÉMICOS (CAMPUS)",
        "DIRECTIVOS FACULTAD O ESCUELA",
        "DIRECTORES DE CARRERA",
        "DIRECTORES DE CARRERA (CAMPUS)",
        "PROFESORES",
        "ADMINISTRATIVOS INSTRUCCIONALES",
      ];
      const reportGroups = [
        "DIRECTIVOS ACADÉMICOS INSTITUCIONALES",
        "DIRECTIVOS ACADÉMICOS (CAMPUS)",
        "DIRECTIVOS FACULTAD O ESCUELA",
        "DIRECTORES DE CARRERA",
        "DIRECTORES DE CARRERA (CAMPUS)",
        "ADMINISTRATIVOS INSTRUCCIONALES",
        // Solo lectura: ven los reportes aunque el resto del grupo
        // "Gestión Curricular y Pedagógica" no se les muestre.
        "VISITANTES",
      ];
      const methodGroups = academicGroups.concat("ESTUDIANTES");
      const inst = this.institution;
      const showReports =
        someGroup(reportGroups) &&
        !!(inst && inst.kl_assessment && inst.show_kl_reports);

      const groups = [
        {
          key: "home",
          icon: "house-door-fill",
          label: gvn("manual.menu.inicio", true, "Inicio"),
          to: "DashboardView",
          primary: true,
        },
        {
          key: "programs",
          icon: "folder-fill",
          label: gvn("manual.menu.crear_programas", true, "Crear Programas"),
          to: "CareerListView",
        },
        {
          key: "products",
          icon: "briefcase-fill",
          label: gvn(
            "manual.menu.productos_curriculares",
            true,
            "Productos Curriculares"
          ),
          groupShow:
            this.isSuper ||
            (this.notProfStudent &&
              (this.isDuoc ||
                (this.isIacc &&
                  this.user &&
                  this.user.group_names.includes(
                    "DIRECTIVOS ACADÉMICOS INSTITUCIONALES"
                  )))),
          items: [
            {
              to: "EgressProfilesList",
              label: gvn("mesh.egressprofile", true, "Perfiles de Egreso"),
              show: this.isSuper,
            },
            {
              to: "CompetenceView",
              label: gvn("mesh.competence", true, "Competencias"),
              show: true,
            },
            {
              to: "MatterListView",
              label: gvn("mesh.egressprofilematter", true, "Asignaturas"),
              show: true,
            },
            {
              to: "RaBaseView",
              label: gvn("teaching.basera", true, "RA Base"),
              show: !!(inst && inst.show_base_ra),
            },
          ],
        },
        {
          key: "administrative",
          icon: "shield-lock-fill",
          label: this.$t("reusable.toolbar.administrativeManagement"),
          groupShow: this.canViewSidebarGroups && this.isDirectivoInst,
          items: [
            {
              to: "AdminInstitutionView",
              label: this.$t("reusable.toolbar.institution"),
              show: this.isSuper,
            },
            {
              to: "AdminUsersView",
              label: this.$t("reusable.toolbar.users"),
              show: this.isSuper,
            },
            {
              to: "SectionView",
              label: gvn(
                "manual.administrar_seccion",
                true,
                "Administrar Secciones"
              ),
              show: !!(inst && inst.kl_assessment) && this.isDirectivoInst,
            },
            {
              to: "ManageEvaluationsView",
              label: this.$t("reusable.toolbar.evaluations"),
              show: !!(inst && inst.kl_assessment) && this.isDirectivoInst,
            },
            {
              to: "AlternativeNamesView",
              label: this.$t("reusable.toolbar.alternativeNames"),
              show: this.isDirectivoInst,
            },
          ],
        },
        {
          key: "pedagogical",
          icon: "clipboard-check",
          label: gvn(
            "manual.gestion_curricular_y_pedagogica",
            false,
            "Gestión Curricular y Pedagógica"
          ),
          // VISITANTES no ven el grupo (canViewSidebarGroups), salvo por Reportes
          groupShow:
            (this.canViewSidebarGroups && someGroup(academicGroups)) ||
            showReports,
          items: [
            {
              to: "ActsView",
              label: gvn("manual.acta_de_comite", true, "Actas de Comités"),
              show: someGroup(academicGroups),
            },
            {
              to: "GeneralReport",
              label: gvn("manual.reporte", true, "Reportes"),
              show: showReports,
            },
          ],
        },
        {
          key: "organizational",
          icon: "diagram-3-fill",
          label: this.$t("reusable.navbar.organizationalDefinitions"),
          items: [
            {
              to: "RegionView",
              label: gvn("common.region", true, "Región"),
              show: this.isDirectivoInst,
            },
            {
              to: "CommuneView",
              label: gvn("common.commune", true, "Comuna"),
              show: this.isDirectivoInst,
            },
            {
              to: "CampusListView",
              label: gvn("mesh.campus", true, "Sedes"),
              show: true,
            },
            {
              to: "FacultyListView",
              label: gvn("mesh.faculty", true, "Facultades"),
              show: true,
            },
            {
              to: "PeriodView",
              label: gvn("mesh.period", true, "Períodos"),
              show: this.notProfStudent,
            },
            {
              to: "TimeAllocationView",
              label: gvn("mesh.timeallocation", true, "Divisiones de tiempo"),
              show: this.isSuper,
            },
            {
              to: "GenericCareerView",
              label: gvn("mesh.genericcareer", true, "Carrera Genérica"),
              show: this.notProfStudent && this.isDuoc,
            },
          ],
        },
        {
          key: "curricular",
          icon: "journal-bookmark-fill",
          label: this.$t("reusable.navbar.curricularDefinitions"),
          items: [
            {
              to: "EgressProfileTypeView",
              label: gvn(
                "mesh.egressprofiletype",
                true,
                "Tipos de Perfiles de Egreso"
              ),
              show: this.notStudent,
            },
            {
              to: "ModalityEgressProfileView",
              label: gvn(
                "mesh.modalityegressprofile",
                false,
                "Modalidad del Perfil de Egreso"
              ),
              show: this.notStudent,
            },
            {
              to: "CompetenceTypeView",
              label: gvn("mesh.competencetype", true, "Tipos de Competencias"),
              show: !this.isDuoc && this.notStudent,
            },
            {
              to: "CyclesListView",
              label: gvn("mesh.cycle", true, "Ciclos Formativos"),
              show: !this.isDuoc && this.notStudent,
            },
            {
              to: "FormationAreaView",
              label: gvn("mesh.formationarea", true, "Áreas De Formación"),
              show: this.notStudent && !this.$hidesFormationArea(this.institution),
            },
            {
              to: "FormationLineView",
              label: gvn("mesh.formationline", true, "Líneas de Formación"),
              show: this.notStudent,
            },
            {
              to: "MatterTypeView",
              label: gvn("mesh.mattertype", true, "Tipos de Asignatura"),
              show: this.notStudent,
            },
            {
              to: "ModalityView",
              label: gvn("mesh.modality", false, "Modalidad de Asignaturas"),
              show: this.notStudent,
            },
            {
              to: "BaseMattersWrapperView",
              label: gvn(
                "mesh.basematterwrapper",
                true,
                "Envoltorio de Asignaturas"
              ),
              show: this.notStudent,
            },
            {
              to: "CertificationView",
              label: gvn(
                "mesh.certification",
                true,
                "Certificación de Credenciales"
              ),
              show: true,
            },
            {
              to: "EvaluationSituationView",
              label: gvn(
                "teaching.evaluationsituation",
                true,
                "Situación Evaluativas"
              ),
              show: this.notStudent,
            },
            {
              to: "RubricPresetView",
              label: gvn("evaluations2.rubricpreset", true, "Modelos de Pauta"),
              show:
                !!(inst && inst.kl_assessment) &&
                this.isDirectivoInst &&
                !this.user.group_names.includes("ESTUDIANTES"),
            },
            {
              to: "StudyEnvironmentView",
              label: gvn(
                "mesh.studyenvironment",
                true,
                "Ambientes de Aprendizajes"
              ),
              show:
                !!(inst && inst.show_study_environment_menu) && this.notStudent,
            },
            {
              to: "ActTypeView",
              label: gvn("cideu.acttype", true, "Tipos de Actas"),
              show: this.notStudent,
            },
            {
              to: "CapacitiesView",
              label: gvn("mesh.capacity", true, "Capacidades"),
              show: this.isDuoc && this.notStudent,
            },
            {
              to: "MatterContributionView",
              label: gvn(
                "mesh.mattercontribution",
                true,
                "Estrategias Curriculares"
              ),
              show:
                !!(inst && inst.show_matter_contribution) && this.notStudent,
            },
            {
              to: "MatterCategoryView",
              label: gvn("teaching.mattercategory", false, "Categoría"),
              show: this.isDuoc && this.notStudent,
            },
            {
              to: "InstructionalMethodView",
              label: this.$t("teaching.instructionalMethod.designTitle"),
              show:
                this.isSuper &&
                this.notStudent &&
                !!(inst && inst.kl_instructional_design),
            },
          ],
        },
        {
          key: "methodological",
          icon: "lightbulb-fill",
          label: gvn(
            "manual.recurso_metodologico",
            true,
            "Recursos Metodológicos"
          ),
          groupShow: this.canViewSidebarGroups && someGroup(methodGroups),
          items: [
            {
              to: "MethodologicalStrategyView",
              label: gvn(
                "mesh.methodologicalstrategy",
                true,
                "Estrategias Metodológicas"
              ),
              show: someGroup(methodGroups),
            },
            {
              to: "DirectTeachingActivityView",
              label: gvn(
                "manual.actividad_presencial",
                true,
                "Actividades Presenciales"
              ),
              show: someGroup(methodGroups),
            },
            {
              to: "AutonomousTeachingActivityView",
              label: gvn(
                "manual.actividad_autonoma",
                true,
                "Actividades Autónomas"
              ),
              show: someGroup(methodGroups),
            },
            {
              to: "ProcedureEvidenceView",
              label: gvn(
                "manual.evidencia_de_desempeño",
                true,
                "Evidencias de Desempeño"
              ),
              show: someGroup(methodGroups),
            },
            {
              to: "TaxonomyView",
              label: gvn("mesh.taxonomy", true, "Taxonomías"),
              show: someGroup(methodGroups),
            },
          ],
        },
      ];

      // Filtra ítems por permiso y descarta grupos sin contenido visible.
      return groups
        .map((g) => {
          if (g.to) return g;
          return { ...g, items: g.items.filter((i) => i.show) };
        })
        .filter((g) => {
          if (g.to) return true;
          if (g.groupShow === false) return false;
          return g.items.length > 0;
        });
    },
    activeGroupKey() {
      const routeName = this.$route.name;
      const group = this.menuGroups.find((g) => {
        if (g.to) return g.to === routeName;
        return g.items.some((i) => i.to === routeName);
      });
      return group ? group.key : null;
    },
  },
  methods: {
    // Acordeón (móvil): abre/cierra un grupo dejando solo uno abierto.
    toggleGroup(key) {
      this.openGroupKey = this.openGroupKey === key ? null : key;
    },
    // Al navegar desde el panel móvil, cerramos el menú.
    handleNavigate() {
      this.expanded = false;
      this.openGroupKey = null;
    },
    // Reposiciona el flyout (solo escritorio) para que no se salga por debajo
    // de la ventana cuando el ícono está en la parte baja del riel y el menú
    // tiene muchas opciones. Ancla el panel al top del ícono por defecto y lo
    // sube lo justo para que su borde inferior quepa en el viewport.
    positionFlyout(event) {
      if (window.innerWidth <= 768) return; // en móvil es un acordeón estático
      const item = event.currentTarget;
      const flyout = item.querySelector(".kl-rail__flyout");
      if (!flyout) return;
      const margin = 12;
      // Reset para medir la altura natural (limitada por su max-height CSS).
      flyout.style.top = "0px";
      const itemTop = item.getBoundingClientRect().top;
      const flyoutHeight = flyout.offsetHeight;
      const viewportHeight = window.innerHeight;
      let topOffset = 0;
      const bottomOverflow = itemTop + flyoutHeight - (viewportHeight - margin);
      if (bottomOverflow > 0) {
        topOffset = -bottomOverflow;
        // No dejar que el borde superior se salga por arriba.
        if (itemTop + topOffset < margin) topOffset = margin - itemTop;
      }
      flyout.style.top = `${topOffset}px`;
    },
  },
  watch: {
    // Avisamos al layout (App.vue) que el riel está expandido para desplazar
    // el contenido y el navbar, en vez de superponerse sobre ellos.
    expanded(val) {
      document.body.classList.toggle("kl-rail-expanded", val);
      if (!val) this.openGroupKey = null;
    },
  },
  beforeDestroy() {
    document.body.classList.remove("kl-rail-expanded");
  },
};
</script>

<style scoped>
.container-kl-menu {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.kl-rail__bottom {
  margin-top: auto;
}
.kl-rail {
  --rail-width: 60px;
  --rail-width-expanded: 248px;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: var(--rail-width);
  z-index: 1060;
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    180deg,
    var(--kl-color-primary-deep) 0%,
    var(--kl-color-primary) 60%,
    var(--kl-color-secondary) 120%
  );
  box-shadow: 4px 0 22px rgba(16, 38, 73, 0.18);
  transition: width 0.22s ease;
}
.kl-rail--expanded {
  width: var(--rail-width-expanded);
}

/* Botón superior (apps / expandir) */
.kl-rail__toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 64px;
  min-height: 64px;
  padding: 0 18px;
  border: none;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  font-size: 1.2rem;
  cursor: pointer;
  transition: background 0.2s ease;
}
.kl-rail__toggle:hover {
  background: rgba(255, 255, 255, 0.22);
}
.kl-rail__toggle:focus {
  outline: none;
}
.kl-rail__brand {
  font-size: 0.95rem;
  font-weight: var(--kl-fw-bold);
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.kl-rail__scroll {
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  gap: 4px;
}

/* Ítem del riel: contenedor. La fila visible vive en .kl-rail__head
(o en .kl-rail__direct para los enlaces directos). Así el flyout puede
fluir DEBAJO del encabezado sin pelearse con el flex de la fila. */
.kl-rail__item {
  position: relative;
}
.kl-rail__head,
.kl-rail__direct {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 0 18px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}
.kl-rail__head:hover,
.kl-rail__direct:hover {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}
.kl-rail__item.is-active > .kl-rail__head,
.kl-rail__direct.is-active {
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
}
.kl-rail__item.is-active > .kl-rail__head::before,
.kl-rail__direct.is-active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #ffffff;
  border-radius: 0 4px 4px 0;
}
/* Acceso principal (Crear Programas): destacado como vista central de la app.
Ícono en tile blanco que resalta sobre el riel oscuro + etiqueta en negrita. */
.kl-rail__direct--primary {
  padding-left: 13px;
  padding-right: 13px;
}
.kl-rail__direct--primary .kl-rail__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #ffffff;
  color: var(--kl-color-primary);
  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.kl-rail__direct--primary:hover .kl-rail__icon {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}
.kl-rail__direct--primary .kl-rail__label {
  font-weight: var(--kl-fw-bold);
}
.kl-rail__direct--primary.is-active .kl-rail__icon {
  background: #ffffff;
  color: var(--kl-color-primary);
}
.kl-rail__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  font-size: 1.15rem;
  flex-shrink: 0;
}
.kl-rail__label {
  white-space: nowrap;
  font-size: 0.9rem;
  font-weight: var(--kl-fw-semibold);
  overflow: hidden;
  text-overflow: ellipsis;
}
.kl-rail__caret {
  margin-left: auto;
  opacity: 0.7;
}

/* Flyout de opciones */
.kl-rail__flyout {
  position: absolute;
  left: 100%;
  top: 0;
  min-width: 256px;
  max-width: 300px;
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  overflow-x: hidden;
  margin-left: 8px;
  padding: 10px;
  background: var(--kl-surface);
  border-radius: 14px;
  box-shadow: 0 18px 45px rgba(16, 38, 73, 0.22);
  opacity: 0;
  visibility: hidden;
  transform: translateX(8px);
  transition: opacity 0.16s ease, transform 0.16s ease, visibility 0.16s;
  z-index: 1200;
}
/* Flyout ancho: cuando hay muchas opciones, se reparten en 2 columnas */
.kl-rail__flyout--wide {
  min-width: 460px;
  max-width: 480px;
}
.kl-rail__flyout-list {
  display: flex;
  flex-direction: column;
}
.kl-rail__flyout--wide .kl-rail__flyout-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 6px;
}
/* Puente invisible para no perder el hover entre el ícono y el flyout.
Va en el ítem (no en el flyout) porque el flyout tiene overflow para el scroll. */
.kl-rail__item::after {
  content: "";
  position: absolute;
  left: 100%;
  top: 0;
  width: 16px;
  height: 100%;
  pointer-events: none;
}
.kl-rail__item:hover::after {
  pointer-events: auto;
}
.kl-rail__item:hover .kl-rail__flyout {
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
}
.kl-rail__flyout-title {
  margin: 4px 8px 8px;
  font-size: var(--kl-fs-caption);
  font-weight: var(--kl-fw-bold);
  letter-spacing: var(--kl-ls-wide);
  text-transform: uppercase;
  text-align: center;
  color: var(--kl-color-primary);
}
.kl-rail__flyout-link {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 9px;
  color: var(--kl-text-body);
  font-size: var(--kl-fs-body);
  font-weight: var(--kl-fw-medium);
  text-decoration: none;
  transition: background 0.16s ease, color 0.16s ease, transform 0.16s ease;
}
.kl-rail__flyout-link:hover {
  background: linear-gradient(
    135deg,
    var(--kl-color-primary) 0%,
    var(--kl-color-secondary) 100%
  );
  color: #fff;
  transform: translateX(2px);
}
.kl-rail__flyout-link.router-link-active {
  background: #eef4f7;
  color: var(--kl-color-primary);
  font-weight: var(--kl-fw-bold);
}
.kl-rail__flyout-chevron {
  margin-right: 6px;
  flex-shrink: 0;
  opacity: 0.6;
}
.kl-rail__flyout-link:hover .kl-rail__flyout-chevron {
  opacity: 1;
}

/* Scrollbar fino dentro del riel expandido */
.kl-rail__scroll::-webkit-scrollbar {
  width: 6px;
}
.kl-rail__scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 6px;
}
/* Backdrop (oculto en escritorio; solo se usa en móvil) */
.kl-rail__backdrop {
  display: none;
}

/* ---- Vista responsive: el riel se colapsa a solo el botón "grid" ----
   No reserva columna; al hacer click se despliega como panel superpuesto. */
@media (max-width: 768px) {
  .kl-rail {
    width: auto;
    height: auto;
    background: transparent;
    box-shadow: none;
  }
  .kl-rail__scroll {
    display: none;
  }
  /* El acceso a IA se oculta por completo en la vista responsive (tanto
  colapsado como expandido); la IA queda disponible solo en escritorio. */
  .kl-rail__bottom {
    display: none;
  }
  /* Botón flotante (solo el ícono grid), alineado con el logo de la fila 1 */
  .kl-rail__toggle {
    margin: 6px 8px;
    width: 44px;
    height: 44px;
    min-height: 44px;
    padding: 0;
    justify-content: center;
    border-radius: 12px;
    background: linear-gradient(
      135deg,
      var(--kl-color-primary) 0%,
      var(--kl-color-secondary) 100%
    );
    color: #fff;
    box-shadow: 0 6px 16px rgba(16, 38, 73, 0.2);
  }
  /* Al expandir: panel de menú a pantalla completa */
  .kl-rail--expanded {
    width: 100%;
    height: 100vh;
    background: linear-gradient(
      180deg,
      var(--kl-color-primary-deep) 0%,
      var(--kl-color-primary) 60%,
      var(--kl-color-secondary) 120%
    );
    box-shadow: 4px 0 22px rgba(16, 38, 73, 0.18);
  }
  .kl-rail--expanded .kl-rail__scroll {
    display: flex;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .kl-rail--expanded .kl-rail__toggle {
    margin: 0;
    width: 100%;
    height: 64px;
    min-height: 64px;
    border-radius: 0;
    justify-content: flex-start;
    padding: 0 18px;
    background: rgba(255, 255, 255, 0.12);
    box-shadow: none;
  }
  /* Acordeón móvil: el flyout se oculta y solo se muestra el grupo abierto.
  Como el .kl-rail__item ahora es un bloque (la fila vive en .kl-rail__head),
  el flyout fluye debajo del encabezado y empuja al siguiente grupo. */
  .kl-rail--expanded .kl-rail__flyout {
    position: static;
    display: none;
    width: 100%;
    margin: 0;
    min-width: 0;
    max-width: none;
    max-height: none;
    overflow: visible;
    padding: 2px 0 8px;
    background: transparent;
    opacity: 1;
    visibility: visible;
    transform: none;
    box-shadow: none;
    border-radius: 0;
  }
  .kl-rail--expanded .kl-rail__item.is-open .kl-rail__flyout {
    display: block;
  }
  /* Siempre 1 columna en el panel móvil (aunque el grupo sea "ancho") */
  .kl-rail--expanded .kl-rail__flyout-list {
    display: flex;
    flex-direction: column;
  }
  /* El título del flyout es redundante con el encabezado del grupo */
  .kl-rail--expanded .kl-rail__flyout-title {
    display: none;
  }
  /* Encabezado del grupo: caret a la derecha que rota al abrir */
  .kl-rail--expanded .kl-rail__caret {
    display: inline-block;
    transition: transform 0.2s ease;
  }
  .kl-rail--expanded .kl-rail__item.is-open .kl-rail__caret {
    transform: rotate(90deg);
  }
  .kl-rail--expanded .kl-rail__item.is-open > .kl-rail__head {
    background: rgba(255, 255, 255, 0.14);
  }
  /* Opciones del acordeón sobre el panel oscuro, con sangría */
  .kl-rail--expanded .kl-rail__flyout-link {
    color: rgba(255, 255, 255, 0.9);
    padding: 9px 14px 9px 40px;
    border-radius: 0;
  }
  .kl-rail--expanded .kl-rail__flyout-link:hover,
  .kl-rail--expanded .kl-rail__flyout-link.router-link-active {
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
    transform: none;
  }
  .kl-rail--expanded .kl-rail__flyout-chevron {
    color: rgba(255, 255, 255, 0.55);
  }
  .kl-rail__item::after {
    display: none;
  }
  .kl-rail__backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(16, 38, 73, 0.45);
    z-index: 1055;
  }
}
</style>
